import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import localeTranslations, { SUPPORTED_LANGUAGES } from './locales';

// Langues supportées
const supportedLanguages = SUPPORTED_LANGUAGES;

// Fonction pour détecter la langue avec logique de priorité personnalisée
const detectLanguage = () => {
  const normalizeLocale = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    return raw.replace(/_/g, '-').trim();
  };

  const getStoredLanguageCandidates = () => {
    const candidates = [];
    try {
      const i18nextLng = normalizeLocale(localStorage.getItem('i18nextLng'));
      if (i18nextLng) candidates.push(i18nextLng);
      const legacyLng = normalizeLocale(localStorage.getItem('language'));
      if (legacyLng) candidates.push(legacyLng);
    } catch {
      // ignore
    }
    return candidates;
  };

  const getNavigatorLanguageCandidates = () => {
    const candidates = [];
    if (typeof navigator === 'undefined') return candidates;
    if (Array.isArray(navigator.languages)) {
      navigator.languages.forEach((lng) => {
        const normalized = normalizeLocale(lng);
        if (normalized) candidates.push(normalized);
      });
    }
    const navLang = normalizeLocale(navigator.language);
    if (navLang) candidates.push(navLang);
    return candidates;
  };

  const langFallback = {
    ca: 'es',
    pt: 'es',
    nl: 'en',
    sv: 'en',
    no: 'en',
    nb: 'en',
    nn: 'en',
    da: 'en',
    fi: 'en',
    pl: 'en',
    cs: 'en',
    sk: 'en',
    hu: 'en',
    ro: 'en',
    bg: 'en',
    el: 'en',
    tr: 'en',
    ru: 'en',
    uk: 'en',
    ar: 'en',
    he: 'en',
    ja: 'en',
    ko: 'en',
    zh: 'en',
  };

  const regionFallback = {
    FR: 'fr',
    BE: 'fr',
    LU: 'fr',
    CH: 'de',
    DE: 'de',
    AT: 'de',
    IT: 'it',
    ES: 'es',
    PT: 'es',
  };

  const pickSupported = (raw) => {
    const normalized = normalizeLocale(raw);
    if (!normalized) return null;

    const lower = normalized.toLowerCase();
    if (supportedLanguages.includes(lower)) return lower;

    const [baseRaw, regionRaw] = normalized.split('-');
    const base = (baseRaw || '').toLowerCase();
    const region = (regionRaw || '').toUpperCase();

    if (supportedLanguages.includes(base)) return base;
    if (region && regionFallback[region] && supportedLanguages.includes(regionFallback[region])) {
      return regionFallback[region];
    }
    if (langFallback[base] && supportedLanguages.includes(langFallback[base])) {
      return langFallback[base];
    }
    return null;
  };

  const candidates = [...getStoredLanguageCandidates(), ...getNavigatorLanguageCandidates()];
  for (const candidate of candidates) {
    const picked = pickSupported(candidate);
    if (picked) return picked;
  }

  return 'en';
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: supportedLanguages.reduce((accumulator, language) => {
      accumulator[language] = {
        translation: localeTranslations[language] || {},
      };
      return accumulator;
    }, {}),
    lng: detectLanguage(), // Utiliser notre fonction de détection personnalisée
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      checkWhitelist: true, // Vérifier que la langue est dans supportedLngs
    },
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
